import DarkIcon from '@/assets/general/moon.svg';
import LightIcon from '@/assets/general/sun.svg';
import SpheronLogo from '@/assets/spheron-specific/spheron-logo.svg';
import { Button, Link } from '@spheron/ui-library';

export type StatusType = 'normal' | 'partial' | 'outage';

export interface IconButtonsProps {
  id: number;
  icon: JSX.Element;
  handleClick: () => void;
}
export enum ThemeMode {
  DARK = 'dark',
  LIGHT = 'light',
  DEFAULT = 'default',
}

export interface LinksProps {
  id: number;
  text: string;
  handleClick: () => void;
}
export interface FooterProps extends React.DOMAttributes<HTMLDivElement> {
  copyrightText: string;
  status?: StatusType;
  statusText?: string;
  statusUrl?: string;
  handleThemeClick?: (theme: ThemeMode) => void;
  iconButtons: IconButtonsProps[];
  links: LinksProps[];
  selectedTheme?: ThemeMode;
  showLogo?: boolean;
  showStatus?: boolean;
  showThemeButtons?: boolean;
}

const FooterUI: React.FC<FooterProps> = ({
  copyrightText,
  status,
  statusText,
  statusUrl,
  handleThemeClick,
  iconButtons,
  links,
  selectedTheme = ThemeMode.DEFAULT,
  showStatus = true,
  showThemeButtons = true,
  showLogo = true,
}) => {
  const themeButtons = [
    {
      id: 1,
      icon: (
        <DarkIcon className="w-5 h-5 text-base-icon dark:text-dark-base-icon" />
      ),
      theme: ThemeMode.DARK,
    },
    {
      id: 2,
      icon: (
        <LightIcon className="w-5 h-5 text-base-icon dark:text-dark-base-icon" />
      ),
      theme: ThemeMode.LIGHT,
    },
    // TODO: Removed this till we remove the dropdown item for theme switch from top navigation
    // {
    //   id: 3,
    //   icon: (
    //     <MonitorIcon className="w-5 h-5 text-base-icon dark:text-dark-base-icon" />
    //   ),
    //   theme: ThemeMode.DEFAULT,
    // },
  ];
  const statusTextClassName = () => {
    switch (status) {
      case 'normal':
        return 'text-base-link dark:text-dark-base-link';
      case 'partial':
        return 'text-action-warning-default dark:text-dark-action-warning-default';
      case 'outage':
        return 'text-action-error-default dark:text-dark-action-error-default';
    }
  };

  const statusBackgroundClassName = () => {
    switch (status) {
      case 'normal':
        return 'bg-base-link dark:bg-dark-base-link';
      case 'partial':
        return 'bg-action-warning-default dark:bg-dark-action-warning-default';
      case 'outage':
        return 'bg-action-error-default dark:bg-dark-action-error-default';
    }
  };

  const statusTextClass = statusTextClassName();
  const statusBackgroundClass = statusBackgroundClassName();
  return (
    <div className="w-full bg-base-bg dark:bg-dark-base-bg flex flex-row h-[68px] px-4 lg:px-14 items-center justify-between border-t border-base-border dark:border-dark-base-border">
      <div className="flex flex-row items-center">
        {showLogo && <SpheronLogo className="w-7 h-8" />}
        <span className="text-base-sub-text-color dark:text-dark-base-sub-text-color text-sm font-normal ml-3">
          {copyrightText}
        </span>
        {showStatus && (
          <a
            href={statusUrl}
            target="blank"
            rel="noreferrer"
            className="ml-5 flex flex-row items-center rounded p-2 hover:bg-cards-hover dark:hover:bg-dark-cards-hover"
          >
            <span
              className={`${statusBackgroundClass} w-2.5 h-2.5 flex rounded-2xl mr-2`}
            />
            <span className={statusTextClass}>{statusText}</span>
          </a>
        )}
      </div>
      <div className="lg:flex lg:flex-row gap-x-12 hidden">
        {links.map((link) => (
          <Link
            key={link.id}
            text={link.text}
            size="default"
            onClick={link.handleClick}
            type="secondary"
            isBold={false}
          />
        ))}
      </div>
      <div className="flex flex-row items-center">
        <div className="flex flex-row gap-x-1.5">
          {iconButtons.map((button) => (
            <Button
              key={button.id}
              buttonType="action"
              size="small"
              onClick={button.handleClick}
              leftIcon={button.icon}
            />
          ))}
        </div>
        {showThemeButtons && (
          <>
            <span className="w-px h-6 bg-base-border dark:bg-dark-base-border mx-3" />
            <div className="flex flex-row gap-x-1.5">
              {themeButtons.map((button) => (
                <Button
                  key={button.id}
                  buttonType="action"
                  size="small"
                  onClick={() => handleThemeClick?.(button.theme)}
                  leftIcon={button.icon}
                  isSelected={selectedTheme === button.theme}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

FooterUI.displayName = 'FooterUI';

export default FooterUI;
